import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltBreadcrumb } from '@cae/cobalt-react/components/breadcrumb';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { UnsavedChangesModal } from '@shared/components/unsaved-changes-modal/UnsavedChangesModal';
import * as React from 'react';

import type { ChangeRequestProps } from './ChangeRequest.types';

import styles from './ChangeRequest.module.css';
import { ChangeRequestForm } from './components/change-request-form/ChangeRequestForm';
import { ChangeRequestMessages } from './components/change-request-messages/ChangeRequestMessages';
import { useChangeRequestForm } from './hooks/use-change-request-form/useChangeRequestForm';
import { useCreateChangeRequest } from './hooks/use-create-change-request/useCreateChangeRequest';

const ChangeRequest = React.memo(
  ({
    isOpen,
    onClose,
    config,
    backNavigationLabel,
  }: Readonly<ChangeRequestProps>): React.ReactElement => {
    const { isLoading, isSuccess, isError, createRequest, reset } =
      useCreateChangeRequest({ config });

    const {
      title,
      description,
      validationErrors,
      showDiscardModal,
      handleTitleChange,
      handleDescriptionChange,
      handleSend,
      handleCloseAttempt,
      handleDiscard,
      handleCancelDiscard,
      handleCreateAnother,
    } = useChangeRequestForm({
      isError,
      isSuccess,
      reset,
      createRequest,
      onClose,
    });

    const content = (
      <div className={styles.container} data-testid="change-request">
        <ChangeRequestMessages
          isSuccess={isSuccess}
          isError={isError}
          onCreateAnother={handleCreateAnother}
        />

        <div className={styles.content}>
          <div className={styles.contentTitle}>
            <h3 className="heading-01">Request summary</h3>
            <p className="body-02">
              Please enter a summary title of the request and then enter as many
              details as you can that will allow us to process the request. All
              fields are required unless specified as optional.
            </p>
          </div>

          <ChangeRequestForm
            title={title}
            description={description}
            onTitleChange={handleTitleChange}
            onDescriptionChange={handleDescriptionChange}
            isLoading={isLoading}
            isSuccess={isSuccess}
            validationErrors={validationErrors}
          />
        </div>
      </div>
    );

    return (
      <>
        <CobaltSidePanel
          backNavigation={
            backNavigationLabel ? (
              <CobaltBreadcrumb
                variant="back-navigation"
                onBackClick={handleCloseAttempt}
              >
                {backNavigationLabel}
              </CobaltBreadcrumb>
            ) : undefined
          }
          closeOnNavigation={false}
          isOpen={isOpen}
          showCloseButton
          size="medium"
          title="Request"
          onClose={handleCloseAttempt}
          actionBar={
            !isSuccess ? (
              <CobaltActionBar
                position="right"
                variant="floating"
                primaryButton={
                  <CobaltButton
                    variant="primary"
                    onClick={handleSend}
                    pending={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send request'}
                  </CobaltButton>
                }
                secondaryButton={
                  <CobaltButton
                    variant="secondary"
                    onClick={handleCloseAttempt}
                  >
                    Cancel
                  </CobaltButton>
                }
              />
            ) : undefined
          }
        >
          {isLoading ? <ContentLoader /> : content}
        </CobaltSidePanel>

        <UnsavedChangesModal
          isOpen={showDiscardModal}
          title="Discard request?"
          description="You will lose all information entered for this request."
          confirmLabel="Discard"
          cancelLabel="Cancel"
          onConfirm={handleDiscard}
          onCancel={handleCancelDiscard}
        />
      </>
    );
  }
);

ChangeRequest.displayName = 'ChangeRequest';

export { ChangeRequest };
